import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, ArchiveRestore, ArrowLeft, Download, KeyRound, Mail, MoreHorizontal, Pencil, Phone, Plus, ShieldOff, Star, Trash2, UserX } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { RepairStatusBadge } from '@/components/repairs';
import { AreaBadge, StatusBadge, Tabs } from '@/components/tickets';
import { Button } from '@/components/ui/button';
import { useConfirm } from '@/components/ui/confirm';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Badge, Card, CardContent, CardHeader, CardTitle, Checkbox, Select, Skeleton } from '@/components/ui/primitives';
import { CustomerContractsCard } from '@/pages/contracts/contracts-page';
import { CustomerSitesCard } from '@/components/customer-sites';
import { EmptyRow, Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { AreaConfig, ContactItem, CustomerDetail, Paginated, RepairListItem, TicketMeta } from '@/lib/types';
import { formatDateTime, formatRelative } from '@/lib/utils';
import { ContactDialog, CustomerDialog } from './customer-form';

const PORTAL_STATUS = { INVITED: 'Eingeladen', ACTIVE: 'Aktiv', DISABLED: 'Gesperrt' } as const;
const ACCESS_LABELS: Record<string, string> = {
  PUBLIC: 'öffentlich',
  REGISTERED: 'alle registrierten Kunden',
  CONTRACT: 'nur Wartungsvertragskunden',
  INDIVIDUAL: 'nur individuell freigeschaltete Kunden',
  INTERNAL: 'nur intern',
  DISABLED: 'deaktiviert',
};

function PortalAccessDialog({ customer, contact, onClose, onSaved }: { customer: CustomerDetail; contact: ContactItem | null; onClose: () => void; onSaved: (c: CustomerDetail) => void }) {
  const [roleKey, setRoleKey] = useState(customer.type === 'BUSINESS' ? 'customer_employee' : 'private_customer');
  const grant = useMutation({
    mutationFn: () => api<CustomerDetail>(`/customers/${customer.id}/contacts/${contact!.id}/portal-access`, { method: 'POST', body: { roleKey } }),
    onSuccess: (c) => {
      toast.success('Portalzugang eingerichtet – Einladung versendet');
      onSaved(c);
      onClose();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <Dialog open={contact !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Portalzugang für {contact?.firstName} {contact?.lastName}</DialogTitle>
          <DialogDescription>Eine Einladung wird an {contact?.email ?? '(keine E-Mail hinterlegt)'} gesendet.</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Select value={roleKey} onChange={(e) => setRoleKey(e.target.value)} aria-label="Portalrolle">
            {customer.type === 'BUSINESS' ? (
              <>
                <option value="customer_employee">Kundenmitarbeiter – sieht eigene Vorgänge</option>
                <option value="customer_admin">Kundenadministrator – sieht alle Vorgänge der Firma, verwaltet Kollegen</option>
              </>
            ) : (
              <option value="private_customer">Privatkunde</option>
            )}
          </Select>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={() => grant.mutate()} loading={grant.isPending} disabled={!contact?.email}>
            Einladen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = useAuth();
  const [tab, setTab] = useState<'contacts' | 'tickets' | 'repairs' | 'sites' | 'portal'>('contacts');
  const [editOpen, setEditOpen] = useState(false);
  const [contactDialog, setContactDialog] = useState<{ open: boolean; contact: ContactItem | null }>({ open: false, contact: null });
  const [portalContact, setPortalContact] = useState<ContactItem | null>(null);
  const [confirmDialog, confirm] = useConfirm();

  const key = ['customers', id];
  const { data: c } = useQuery({ queryKey: key, queryFn: () => api<CustomerDetail>(`/customers/${id}`) });
  const { data: areas } = useQuery({
    queryKey: ['ticket-config', 'areas'],
    queryFn: () => api<AreaConfig[]>('/ticket-config/areas'),
    enabled: can('tickets.configure'),
  });
  const { data: meta } = useQuery({ queryKey: ['tickets', 'meta'], queryFn: () => api<TicketMeta>('/tickets/meta'), enabled: can('tickets.read') });
  const { data: repairs } = useQuery({
    queryKey: ['repairs', 'customer', id],
    queryFn: () => api<Paginated<RepairListItem>>(`/repairs?state=all&pageSize=50&customerId=${id}`),
    enabled: can('repairs.read'),
  });

  const update = (d: CustomerDetail) => {
    qc.setQueryData(key, d);
    qc.invalidateQueries({ queryKey: ['customers', 'list'] });
  };
  const onError = (e: unknown) => toast.error(errorMessage(e));

  const archive = useMutation({ mutationFn: (archived: boolean) => api<CustomerDetail>(`/customers/${id}/archive`, { method: 'POST', body: { archived } }), onSuccess: update, onError });
  const anonymize = useMutation({
    mutationFn: () => api(`/customers/${id}/anonymize`, { method: 'POST', body: { confirm: true } }),
    onSuccess: () => {
      toast.success('Personenbezogene Daten wurden gelöscht');
      qc.invalidateQueries({ queryKey: ['customers'] });
    },
    onError,
  });
  const contactAction = useMutation({
    mutationFn: ({ contactId, action }: { contactId: string; action: 'delete' | 'revoke' }) =>
      api<CustomerDetail>(`/customers/${id}/contacts/${contactId}${action === 'revoke' ? '/portal-access' : ''}`, { method: 'DELETE' }),
    onSuccess: update,
    onError,
  });
  const grants = useMutation({ mutationFn: (areaIds: string[]) => api<CustomerDetail>(`/customers/${id}/area-grants`, { method: 'PUT', body: { areaIds } }), onSuccess: update, onError });

  if (!c) return <Skeleton className="h-96" />;
  const canUpdate = can('customers.update');
  const address = [c.street, [c.postalCode, c.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const restrictedAreas = areas?.filter((a) => a.portalAccess === 'INDIVIDUAL' || a.portalAccess === 'CONTRACT') ?? [];

  return (
    <>
      {confirmDialog}
      <Link to="/customers" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="size-4" /> Kunden
      </Link>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm text-muted-foreground">{c.number}</span>
            <Badge variant="secondary">{c.type === 'BUSINESS' ? 'Geschäftskunde' : 'Privatkunde'}</Badge>
            {c.archivedAt && <Badge variant="warning">archiviert</Badge>}
            {c.erpNumber && (
              <Badge variant="outline" title={c.erpSyncedAt ? `Zuletzt mit Powerbird abgeglichen: ${formatDateTime(c.erpSyncedAt)}` : undefined}>
                Powerbird {c.erpNumber}
              </Badge>
            )}
          </div>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">{c.name}</h1>
          {c.openErpChange && (
            <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">
              {c.openErpChange.kind === 'NEW' ? 'Noch nicht in Powerbird – die Buchhaltung ist informiert.' : `Änderungen (${c.openErpChange.fields.join(', ')}) sind der Buchhaltung zum Nachtragen in Powerbird gemeldet.`}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {can('tickets.create') && !c.archivedAt && (
            <Button asChild variant="outline" size="sm">
              <Link to={`/tickets/new?customerId=${c.id}&customerName=${encodeURIComponent(c.name)}`}>
                <Plus /> Ticket
              </Link>
            </Button>
          )}
          {canUpdate && (
            <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
              <Pencil /> Bearbeiten
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="outline" className="size-8" aria-label="Weitere Aktionen">
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <a href={`/api/customers/${c.id}/export`} download>
                  <Download /> Datenauskunft (JSON)
                </a>
              </DropdownMenuItem>
              {canUpdate && (
                <DropdownMenuItem onSelect={() => archive.mutate(!c.archivedAt)}>
                  {c.archivedAt ? <ArchiveRestore /> : <Archive />} {c.archivedAt ? 'Reaktivieren' : 'Archivieren'}
                </DropdownMenuItem>
              )}
              {can('customers.delete') && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    destructive
                    onSelect={async () => {
                      const ok = await confirm({
                        title: `Personenbezogene Daten von ${c.name} löschen?`,
                        description:
                          'Name, Kontaktdaten, Ansprechpartner und Portalzugänge werden unwiderruflich entfernt (DSGVO Art. 17). Tickets bleiben ohne Personenbezug erhalten.',
                        confirmLabel: 'Endgültig löschen',
                        destructive: true,
                      });
                      if (ok) anonymize.mutate();
                    }}
                  >
                    <Trash2 /> Daten löschen (anonymisieren)
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[20rem_minmax(0,1fr)] 3xl:grid-cols-[24rem_minmax(0,1fr)]">
        <div className="grid h-fit gap-4">
        <Card className="h-fit">
          <CardContent className="grid gap-3 pt-5 text-sm">
            {c.email && (
              <a href={`mailto:${c.email}`} className="flex items-center gap-2 hover:underline">
                <Mail className="size-4 text-muted-foreground" /> {c.email}
              </a>
            )}
            {(c.phone || c.mobile) && (
              <p className="flex items-center gap-2">
                <Phone className="size-4 text-muted-foreground" /> {[c.phone, c.mobile].filter(Boolean).join(' · ')}
              </p>
            )}
            {address && <p className="text-muted-foreground">{address}</p>}
            {c.vatId && <p className="text-muted-foreground">USt-IdNr. {c.vatId}</p>}
            {c.notes && <p className="whitespace-pre-wrap rounded-md bg-muted/50 p-2 text-xs">{c.notes}</p>}
            <dl className="mt-2 grid grid-cols-2 gap-2 border-t pt-3 text-xs">
              <dt className="text-muted-foreground">Offene Tickets</dt>
              <dd className="text-right font-medium">{c.openTickets}</dd>
              <dt className="text-muted-foreground">Tickets gesamt</dt>
              <dd className="text-right font-medium">{c.ticketCount}</dd>
              <dt className="text-muted-foreground">Kunde seit</dt>
              <dd className="text-right">{formatDateTime(c.createdAt)}</dd>
            </dl>
          </CardContent>
        </Card>
        <CustomerContractsCard customer={{ id: c.id, name: c.name }} />
        </div>

        <div className="min-w-0">
          <Tabs
            value={tab}
            onChange={setTab}
            items={[
              { value: 'contacts', label: `Ansprechpartner (${c.contacts.length})` },
              { value: 'tickets', label: `Tickets (${c.ticketCount})` },
              ...(can('repairs.read') ? [{ value: 'repairs' as const, label: `Reparaturen (${repairs?.total ?? 0})` }] : []),
              ...(can('monitoring.read') ? [{ value: 'sites' as const, label: 'RMM-Standorte' }] : []),
              ...(can('tickets.configure') && canUpdate ? [{ value: 'portal' as const, label: 'Bereichsfreigaben' }] : []),
            ]}
          />
          <div className="pt-4">
            {tab === 'contacts' && (
              <Card>
                {canUpdate && (
                  <div className="flex justify-end border-b p-3">
                    <Button size="sm" variant="outline" onClick={() => setContactDialog({ open: true, contact: null })}>
                      <Plus /> Ansprechpartner
                    </Button>
                  </div>
                )}
                <Table>
                  <THead>
                    <TR className="hover:bg-transparent">
                      <TH>Name</TH>
                      <TH className="hidden md:table-cell">Kontakt</TH>
                      <TH>Portal</TH>
                      <TH className="w-12" />
                    </TR>
                  </THead>
                  <TBody>
                    {c.contacts.length === 0 && <EmptyRow colSpan={4}>Noch keine Ansprechpartner.</EmptyRow>}
                    {c.contacts.map((p) => (
                      <TR key={p.id}>
                        <TD>
                          <p className="flex items-center gap-1.5 font-medium">
                            {p.firstName} {p.lastName}
                            {p.isPrimary && <Star className="size-3.5 fill-amber-400 text-amber-400" aria-label="Hauptansprechpartner" />}
                          </p>
                          {p.position && <p className="text-xs text-muted-foreground">{p.position}</p>}
                        </TD>
                        <TD className="hidden text-muted-foreground md:table-cell">
                          <p>{p.email ?? '–'}</p>
                          <p className="text-xs">{[p.phone, p.mobile].filter(Boolean).join(' · ')}</p>
                        </TD>
                        <TD>
                          {p.portal ? (
                            <div>
                              <Badge variant={p.portal.status === 'ACTIVE' ? 'success' : p.portal.status === 'INVITED' ? 'warning' : 'destructive'}>
                                {PORTAL_STATUS[p.portal.status]}
                              </Badge>
                              <p className="mt-0.5 text-xs text-muted-foreground">{p.portal.roles.join(', ')}</p>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">kein Zugang</span>
                          )}
                        </TD>
                        <TD>
                          {canUpdate && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="size-8" aria-label="Aktionen">
                                  <MoreHorizontal />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onSelect={() => setContactDialog({ open: true, contact: p })}>
                                  <Pencil /> Bearbeiten
                                </DropdownMenuItem>
                                {can('customers.portal_access') && (!p.portal || p.portal.status === 'DISABLED') && (
                                  <DropdownMenuItem disabled={!p.email} onSelect={() => setPortalContact(p)}>
                                    <KeyRound /> Portalzugang einrichten
                                  </DropdownMenuItem>
                                )}
                                {can('customers.portal_access') && p.portal && p.portal.status !== 'DISABLED' && (
                                  <DropdownMenuItem onSelect={() => contactAction.mutate({ contactId: p.id, action: 'revoke' })}>
                                    <ShieldOff /> Portalzugang sperren
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  destructive
                                  onSelect={async () => {
                                    if (await confirm({ title: `${p.firstName} ${p.lastName} entfernen?`, description: p.portal ? 'Der Portalzugang wird gesperrt.' : undefined, confirmLabel: 'Entfernen', destructive: true })) {
                                      contactAction.mutate({ contactId: p.id, action: 'delete' });
                                    }
                                  }}
                                >
                                  <UserX /> Entfernen
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </Card>
            )}

            {tab === 'tickets' && (
              <Card>
                <Table>
                  <TBody>
                    {c.recentTickets.length === 0 && <EmptyRow colSpan={3}>Keine Tickets.</EmptyRow>}
                    {c.recentTickets.map((t) => (
                      <TR key={t.id} className="cursor-pointer" onClick={() => navigate(`/tickets/${t.id}`)}>
                        <TD>
                          <span className="font-mono text-xs text-muted-foreground">{t.number}</span>
                          <p className="font-medium">{t.subject}</p>
                        </TD>
                        <TD className="hidden sm:table-cell">
                          <AreaBadge name={t.area.name} color={t.area.color} />
                        </TD>
                        <TD>
                          <StatusBadge name={t.status.name} color={t.status.color} />
                          <p className="mt-1 text-xs text-muted-foreground">{formatRelative(t.lastActivityAt)}</p>
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
                {c.ticketCount > c.recentTickets.length && meta && (
                  <div className="border-t p-3 text-sm">
                    <Link to={`/tickets?state=all&customerId=${c.id}`} className="text-primary hover:underline">
                      Alle {c.ticketCount} Tickets anzeigen
                    </Link>
                  </div>
                )}
              </Card>
            )}

            {tab === 'repairs' && (
              <Card>
                {can('repairs.intake') && !c.archivedAt && (
                  <div className="flex justify-end border-b p-3">
                    <Button asChild size="sm" variant="outline">
                      <Link to={`/repairs/new?customerId=${c.id}&customerName=${encodeURIComponent(c.name)}`}>
                        <Plus /> Gerät annehmen
                      </Link>
                    </Button>
                  </div>
                )}
                <Table>
                  <TBody>
                    {repairs?.items.length === 0 && <EmptyRow colSpan={3}>Keine Reparaturen.</EmptyRow>}
                    {repairs?.items.map((r) => (
                      <TR key={r.id} className="cursor-pointer" onClick={() => navigate(`/repairs/${r.id}`)}>
                        <TD>
                          <span className="font-mono text-xs text-muted-foreground">{r.number}</span>
                          <p className="font-medium">{r.device.label}</p>
                        </TD>
                        <TD className="hidden max-w-xs truncate text-muted-foreground sm:table-cell">{r.faultDescription}</TD>
                        <TD className="text-right">
                          <RepairStatusBadge status={r.status} />
                          <p className="mt-1 text-xs text-muted-foreground">{formatRelative(r.receivedAt)}</p>
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </Card>
            )}

            {tab === 'sites' && <CustomerSitesCard customerId={c.id} />}

            {tab === 'portal' && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Freischaltung für eingeschränkte Bereiche</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-2">
                  {restrictedAreas.length === 0 && (
                    <p className="text-sm text-muted-foreground">Kein Ticketbereich ist auf individuell freigeschaltete Kunden beschränkt.</p>
                  )}
                  {restrictedAreas.map((a) => {
                    const checked = c.areaGrantIds.includes(a.id);
                    return (
                      <label key={a.id} className="flex items-start gap-3 rounded-md border p-3">
                        <Checkbox
                          className="mt-0.5"
                          checked={checked}
                          onCheckedChange={(v) => grants.mutate(v ? [...c.areaGrantIds, a.id] : c.areaGrantIds.filter((x) => x !== a.id))}
                        />
                        <span>
                          <span className="block text-sm font-medium">{a.name}</span>
                          <span className="text-xs text-muted-foreground">Bereich: {ACCESS_LABELS[a.portalAccess]}</span>
                        </span>
                      </label>
                    );
                  })}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      <CustomerDialog customer={c} open={editOpen} onOpenChange={setEditOpen} onSaved={update} />
      <ContactDialog customerId={c.id} contact={contactDialog.contact} open={contactDialog.open} onOpenChange={(open) => setContactDialog((d) => ({ ...d, open }))} onSaved={update} />
      <PortalAccessDialog customer={c} contact={portalContact} onClose={() => setPortalContact(null)} onSaved={update} />
    </>
  );
}
